import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDateTime, stat } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Inbox as InboxIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "@/context/I18nContext";

const PRIO: Record<string, string> = { normal: "info", important: "warn", urgent: "critical" };

export default function Inbox() {
  const {
    t
  } = useI18n();

  const [messages, setMessages] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<any>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/comm/messages/inbox")
      .then((r) => { setMessages(r.data.messages); setUnread(r.data.unread); })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (m: any) => {
    setActive(m);
    if (!m.read) {
      try { await api.post(`/comm/messages/${m.id}/read`); load(); } catch { /* ignore */ }
    }
  };

  const ack = async () => {
    try {
      await api.post(`/comm/messages/${active.id}/ack`);
      toast.success("Acknowledged");
      setActive({ ...active, acknowledged: true });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    (<div>
      <PageHeader title="Inbox" subtitle={unread > 0 ? `${unread} unread` : t("All caught up")} testId="inbox-header" />
      {loading ? <LoadingBlock /> : messages.length === 0 ? (
        <EmptyState icon={<InboxIcon size={28} />} title="No messages" />
      ) : (
        <Card className="overflow-hidden" data-testid="inbox-list">
          {messages.map((m) => (
            <button key={m.id} onClick={() => open(m)} data-testid={`inbox-msg-${m.id}`}
              className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left last:border-0 hover:bg-muted/40">
              <span className={`h-2 w-2 shrink-0 rounded-full ${m.read ? "bg-transparent" : "bg-brand"}`} />
              <div className="min-w-0 flex-1">
                <div className={`truncate ${m.read ? "font-medium" : "font-semibold"}`}>{m.title}</div>
                <div className="truncate text-xs text-muted-foreground">{m.body}</div>
              </div>
              <div className="flex items-center gap-2">
                {m.requires_ack && (m.acknowledged
                  ? <span className="flex items-center gap-1 text-xs text-status-good"><CheckCircle2 size={13} />{t("Acked")}</span>
                  : <span className="text-xs font-medium text-status-warn">{t("Ack needed")}</span>)}
                <StatusPill status={PRIO[m.priority] || "info"}>{stat(m.priority)}</StatusPill>
              </div>
            </button>
          ))}
        </Card>
      )}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="message-dialog">
          <DialogHeader><DialogTitle>{active?.title}</DialogTitle></DialogHeader>
          <div className="whitespace-pre-wrap text-sm">{active?.body}</div>
          <div className="mt-1 text-xs text-muted-foreground">{t("Priority:")}{" "}{active?.priority}{active?.created_at ? ` · ${fmtDateTime(active.created_at)}` : ""}</div>
          <DialogFooter>
            {active?.requires_ack && !active?.acknowledged && (
              <Button onClick={ack} data-testid="ack-button"><CheckCircle2 size={15} className="mr-1.5" />{t("Acknowledge")}</Button>
            )}
            {active?.acknowledged && <span className="flex items-center gap-1 text-sm text-status-good"><CheckCircle2 size={15} />{t("Acknowledged")}</span>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>)
  );
}
